/** Copyright (c) 2022-present TVM Engineering Authors. */

import React, { Suspense } from "react";
import { Route, Switch } from "react-router-dom";
import LoadingScreen from "./components/feedback/LoadingScreen";
import STATIC_ROUTES from "./route/STATIC_ROUTES";
// Import Higher Order Components
import RequireAuth from "./route/RequireAuth";
import ErrorBoundary from "./route/ErrorBoundary";

// const Login = React.lazy(() => import("./containers/auth/SignIn"));
const Logout = React.lazy(() => import("./containers/auth/Logout"));
const ConfirmRegistration = React.lazy(() => import("./containers/auth/ConfirmRegistration"));
const RegistrationForward = React.lazy(() => import("./containers/auth/RegistrationForward"));
const ForgotPassword = React.lazy(() => import("./containers/auth/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./containers/auth/ResetPassword"));
const RemoveUser = React.lazy(() => import("./containers/auth/RemoveUser"));

const LandingPage = React.lazy(() => import("./pages/LandingPage/LandingPage"));
const SignUpPage = React.lazy(() => import("./pages/SignUp/SingUpPage"));
const ProjectPage = React.lazy(() => import("./pages/ProjectPage"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage/ProfilePage"));
const TermsAndConditionsPage = React.lazy(() =>
  import("./pages/TermsAndConditionsPage/TermsAndConditionsPage")
);
const ProjectsTablePage = React.lazy(() => import("./pages/ProjectTablePage"));
const AdminDashboard = React.lazy(() => import("./pages/AdminPage/AdminDashboard"));
const NotFoundPage = React.lazy(() => import("./pages/StaticPages/NotFoundPage"));
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const SupportPage = React.lazy(() => import("./pages/SupportPage"));
const UploadsPage = React.lazy(() => import("./pages/UploadsPage/UploadsPage"));
const DownloadHistoryPage = React.lazy(() => import("./pages/DownloadHistoryPage/DownloadHistory"));
const SubscriptionHistoryPage = React.lazy(() =>
  import("./pages/Subscription/SubscriptionHistory")
);
const RegistrationHistoryPage = React.lazy(() =>
  import("./pages/RegistrationHistory/RegistrationHistory")
);
const Maintenance = React.lazy(() => import("./pages/Maintenance/Maintenance"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const Imprint = React.lazy(() => import("./pages/ImprintPage"));
const termsGeneral = React.lazy(() => import("./pages/StaticPages/TermsGeneralPage"));
const ShareDownloadLinkPage = React.lazy(() =>
  import("./pages/ShareDownloadLink/ShareDownloadLink")
);
const NoUser = React.lazy(() => import("./components/NoUser"));

const ROLES = { user: "user", super_admin: "super-admin", admin: "admin" };

const Routes = React.memo((props: any) => {
  const { user } = props;
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
        <Switch>
          <Route exact path={STATIC_ROUTES.landingPage} component={LandingPage} />

          <Route exact path={STATIC_ROUTES.signup} component={SignUpPage} />

          {/* <Route exact path={STATIC_ROUTES.login} component={Login} /> */}
          <Route path={STATIC_ROUTES.logout} component={Logout} />
          <Route path={STATIC_ROUTES.confirmRegistration} component={ConfirmRegistration} />
          <Route path={STATIC_ROUTES.removeUser} component={RemoveUser} />
          <Route path={STATIC_ROUTES.RegistrationForward} component={RegistrationForward} />
          <Route
            path={STATIC_ROUTES.dashboard}
            component={RequireAuth(DashboardPage, [ROLES.super_admin, ROLES.admin, ROLES.user])}
          />
          <Route
            path={STATIC_ROUTES.support}
            component={RequireAuth(SupportPage, [ROLES.super_admin, ROLES.admin, ROLES.user])}
          />
          <Route path={STATIC_ROUTES.forgotPassword} component={ForgotPassword} />
          <Route path={STATIC_ROUTES.resetPassword} component={ResetPassword} />
          <Route path={STATIC_ROUTES.privacyPolicy} component={PrivacyPolicy} />
          <Route path={STATIC_ROUTES.Imprint} component={Imprint} />
          <Route path={STATIC_ROUTES.termsGeneral} component={termsGeneral} />

          {/* <Route exact path={STATIC_ROUTES.terms} component={TermsOfService}/> */}
          {/* <Route exact path={STATIC_ROUTES.privacy} component={PrivacyPolicy}/> */}
          {/* <Route exact path={STATIC_ROUTES.cookie} component={CookiePage}/> */}
          <Route
            exact
            path={STATIC_ROUTES.projects}
            component={RequireAuth(ProjectPage, [ROLES.super_admin, ROLES.admin, ROLES.user])}
          />
          <Route
            exact
            path={STATIC_ROUTES.profile}
            component={RequireAuth(ProfilePage, [ROLES.super_admin, ROLES.admin, ROLES.user])}
          />
          <Route
            exact
            path={STATIC_ROUTES.uploads}
            component={RequireAuth(UploadsPage, [ROLES.super_admin, ROLES.admin])}
          />
          <Route
            exact
            path={STATIC_ROUTES.downloadHistory}
            component={RequireAuth(DownloadHistoryPage, [ROLES.super_admin, ROLES.admin])}
          />
          <Route
            exact
            path={STATIC_ROUTES.maintenance}
            component={RequireAuth(Maintenance, [ROLES.super_admin, ROLES.admin])}
          />
          <Route
            exact
            path={STATIC_ROUTES.subscriptionHistory}
            component={RequireAuth(SubscriptionHistoryPage, [ROLES.super_admin, ROLES.admin])}
          />
          <Route
            exact
            path={STATIC_ROUTES.registrationHistory}
            component={RequireAuth(RegistrationHistoryPage, [ROLES.super_admin, ROLES.admin])}
          />
          <Route
            exact
            path={STATIC_ROUTES.shareDownloadLink}
            // component={RequireAuth(ShareDownloadLinkPage, [
            //   ROLES.super_admin,
            //   ROLES.admin,
            //   ROLES.user,
            // ])}
            component={user ? ShareDownloadLinkPage : NoUser}
          />

          <Route
            exact
            path={STATIC_ROUTES.projectsTable}
            component={RequireAuth(ProjectsTablePage, [ROLES.super_admin, ROLES.admin])}
          />
          {/* Admin Panel */}
          <Route
            exact
            path={STATIC_ROUTES.admin}
            component={RequireAuth(AdminDashboard, [ROLES.super_admin, ROLES.admin])}
          />
          <Route
            exact
            path={STATIC_ROUTES.termsAndConditions}
            component={RequireAuth(TermsAndConditionsPage, [ROLES.super_admin, ROLES.admin])}
          />

          <Route path="*" component={NotFoundPage} />
          <Route path={STATIC_ROUTES.errorPage} component={NotFoundPage} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
});

export default Routes;
